import React from "react";
import { useEffect, useRef } from "react";

import { css } from "@emotion/core";
import {
  compact,
  filter,
  indexOf,
  map,
  pipe,
  prop,
  sortBy,
  toPairs,
} from "lodash/fp";

import { Alert, Typography } from "@zapier/design-system";

import { FieldError } from "@zapier/payment/esm/types";
import { scrollTo } from "@zapier/payment/esm/utils/scrollTo";

const FIELDS_ORDER = [
  "number",
  "name",
  "expiry",
  "cvc",
  "addressCountry",
  "addressState",
  "addressZip",
];

const errorListCss = css`
  margin: 0;
`;

const toErrorsArray = pipe([
  toPairs,
  sortBy(([fieldName]) => indexOf(fieldName, FIELDS_ORDER)),
  map("1"),
  filter(Boolean),
]);

const countFixedErrors = (errors: FieldError[]): number =>
  pipe([map("isFixed"), compact, prop("length")])(errors);

const getMessageBarKind = (errors: FieldError[], fixedErrorsCount: number) =>
  fixedErrorsCount === errors.length ? "success" : "error";

type Props = {
  /** Array of Field Errors to show */
  errors: FieldError[];
};

const textCss = (isFixed = false) => ({
  ...Typography.paragraph3,
  textDecoration: isFixed ? "line-through" : "none",
});

const renderError = (error: FieldError) => (
  <li css={textCss(error.isFixed)} key={error.message}>
    {error.message}
  </li>
);

const CreditCardErrors: React.FC<Props> = props => {
  const rootRef = useRef<HTMLDivElement>(null);
  const errors = toErrorsArray(props.errors);
  const fixedErrorsCount = countFixedErrors(errors);
  const title =
    fixedErrorsCount < errors.length
      ? "We're almost there. The following items need your attention:"
      : "Everything looks good now!";

  useEffect(() => {
    scrollTo(rootRef.current); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={rootRef}>
      <Alert title={title} type={getMessageBarKind(errors, fixedErrorsCount)}>
        {Array.isArray(errors) && errors.length > 0 && (
          <ul css={errorListCss}>{map(renderError, errors)}</ul>
        )}
      </Alert>
    </div>
  );
};

export default CreditCardErrors;
